/**
 * The rules tier selector (docs/design/02 §2.1): defaults to the player's `play.profile` default tier
 * (tier 2 unless changed, owner decision Q5). Tier 3 is hidden while it is disabled (Q1).
 */
import { Field, LOOK, Segmented, cn } from '@play/ui';
import { ENABLED_TIERS, type EnabledTier } from '../../lib/env';

export const TIER_INFO: Record<EnabledTier, { name: string; blurb: string }> = {
  1: { name: 'Barebones', blurb: 'The shared playmat. You and your opponent run the rules.' },
  2: {
    name: 'Recommendation',
    blurb: 'The playmat plus a rules advisor: highlights, reminders, automatic bookkeeping.',
  },
};

/** The tier choice as Poroboard's segmented control, with the chosen tier's blurb under it. */
export function TierPicker({
  value,
  onChange,
  className,
}: {
  value: EnabledTier;
  onChange: (t: EnabledTier) => void;
  className?: string;
}) {
  return (
    <Field label="Rules tier" className={cn('w-full sm:w-[420px]', className)}>
      <Segmented
        label="Rules tier"
        value={String(value)}
        onChange={(v) => {
          onChange(enabledTier(Number(v)));
        }}
        options={ENABLED_TIERS.map(
          (t) =>
            [
              String(t),
              <span key={t} data-testid={`tier-${t}`}>
                Tier {t} · {TIER_INFO[t].name}
              </span>,
            ] as const,
        )}
      />
      <span className="text-[11.5px] leading-4 mt-0.5" style={{ color: LOOK.muted }}>
        {TIER_INFO[value].blurb}
      </span>
    </Field>
  );
}

/** Clamps any stored tier to an enabled one. */
export function enabledTier(t: number): EnabledTier {
  return (ENABLED_TIERS as readonly number[]).includes(t) ? (t as EnabledTier) : 2;
}
